// ====================================================================================
// WARD PSYCHOLOGY SYNTHESIS ENGINE
// ====================================================================================

import { useMemo, useState } from "react";
// Removed 'Panel' from imports
import { ScriptTag } from "../../UtilityComponents"; 

import { motion, AnimatePresence } from "framer-motion";

// UPDATED IMPORTS: Added Copy and Check
import { Brain, Sparkles, Compass, Split, Maximize2, RadioTower, X, MapPin, AlertTriangle, Quote, Info, BookOpen, PlayCircle, List, Megaphone, GitMerge, MessageCircle, Tags, Copy, Check } from "lucide-react";

// ====================================================================================
// HELPER: GLASS CONTAINER (Unified Aesthetic)
// ====================================================================================
const GlassContainer = ({ title, icon: Icon, children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl relative flex flex-col ${className}`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800/50 flex items-center gap-2">
            {Icon && <Icon size={16} className="text-indigo-400" />}
            <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest">{title}</h3>
        </div>
        {/* Content */}
        <div className="p-4 flex-grow">
            {children}
        </div>
    </div>
);

const generateWardNarrative = (archetypes) => {
  if (!archetypes || archetypes.length === 0) return null;

  // 1. ANALYZE THE DATA
  const sortedByPop = [...archetypes].sort((a, b) => b.ward_representation_percentage - a.ward_representation_percentage);
  const dominant = sortedByPop[0];
  const secondary = sortedByPop[1];
  
  const sortedByPersuasion = [...archetypes].sort((a, b) => 
    (b.campaign_targeting?.persuadability_score || 0) - (a.campaign_targeting?.persuadability_score || 0)
  );
  const topTarget = sortedByPersuasion[0];

  const totalPop = archetypes.reduce((acc, curr) => acc + (curr.ward_representation_percentage || 0), 0);
  
  // 2. CONSTRUCT THE PSYCHOLOGICAL PROFILE
  // Check Big 5 Traits of dominant group to determine "Ward Personality"
  const ocean = dominant.psychographics?.ocean_profile || {};
  const personalityType = 
    ocean.openness > 0.7 && ocean.conscientiousness > 0.7 ? "Intellectual-Structured" :
    ocean.openness > 0.7 ? "Progressive-Open" :
    ocean.conscientiousness > 0.7 ? "Traditional-Orderly" : "Reactive-Fluid";

  // Determine the Driving Tension (Dominant Motivation vs Target Motivation)
  const primaryDriver = dominant.psychographics?.vals_profile?.primary_motivation || "Ideals";
  const targetDriver = topTarget.psychographics?.vals_profile?.primary_motivation || "Survival";

  // 3. GENERATE THE "LECTURE" (Templates)
  
  const intro = `This ward presents a distinct **${personalityType}** psychological footprint. Dominated by the **${dominant.name}** segment (${dominant.ward_representation_percentage.toFixed(0)}% of voters), the collective psyche is driven primarily by **${primaryDriver}**, prioritizing long-term stability and competence over radical change.`;

  const tension = `However, the strategic opportunity lies in the psychological divergence. While the majority seeks validation of their values, your primary persuasion target—the **${topTarget.name}** (${topTarget.ward_representation_percentage.toFixed(0)}%)—operates under a **${targetDriver}** motivation. They are less influenced by the abstract ideals of the majority and more reactive to tangible, immediate stressors like ${Object.keys(topTarget.political_profile?.issue_priorities || {}).slice(0,2).join(' and ').replace(/_/g, ' ')}.`;

  const advice = `**Psychological Strategy:** Do not use the same rhetoric for both. The ward's "Superego" (the ${dominant.name}) requires high-fidelity, intellectual arguments (Logos/Ethos). The ward's "Id" (the ${topTarget.name}) requires emotional resonance and relief-based messaging (Pathos). Bridging this gap is your key to unlocking the margin.`;

  return { intro, tension, advice, personalityType, primaryDriver };
};

export const WardPsychologyLecture = ({ archetypes }) => {
    const analysis = useMemo(() => generateWardNarrative(archetypes), [archetypes]);
    
    if (!analysis) return null;

    // UPDATED: Standardized Glass Aesthetic
    return (
        <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-6 relative overflow-hidden h-full">
            {/* Ambient Background Effect */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
            
            <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                    <Brain size={20} className="text-indigo-400" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-white leading-none">Psychometric Ward Profile</h3>
                    <p className="text-xs text-slate-400 mt-1 uppercase tracking-widest">Deep-Dive Synthesis</p>
                </div>
            </div>

            <div className="space-y-6 relative z-10">
                
                {/* 1. The Summary */}
                <div className="prose prose-invert prose-sm max-w-none">
                    <p className="text-slate-300 leading-relaxed text-sm">
                        <span dangerouslySetInnerHTML={{ __html: analysis.intro.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                    </p>
                </div>

                {/* 2. The Diamond (Conflict) */}
                <div className="bg-slate-800/40 p-4 rounded-lg border-l-2 border-amber-500">
                    <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2 flex items-center">
                        <Split size={12} className="mr-2" /> The Divergence
                    </h4>
                    <p className="text-slate-300 leading-relaxed text-sm">
                         <span dangerouslySetInnerHTML={{ __html: analysis.tension.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                    </p>
                </div>

                {/* 3. The Recommendation */}
                <div className="bg-indigo-900/20 p-4 rounded-lg border border-indigo-500/20">
                    <div className="flex items-start gap-3">
                        <Sparkles size={16} className="text-indigo-400 mt-1 flex-shrink-0" />
                        <p className="text-indigo-200 leading-relaxed text-sm italic">
                             <span dangerouslySetInnerHTML={{ __html: analysis.advice.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white not-italic">$1</strong>') }} />
                        </p>
                    </div>
                </div>

                {/* 4. Cognitive Drivers (Visual Tags) */}
                <div className="pt-4 border-t border-slate-700/50">
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-slate-800/50 text-slate-400 text-xs rounded-full border border-slate-700/50">
                            Primary Motivation: <span className="text-white font-bold">{analysis.primaryDriver}</span>
                        </span>
                        <span className="px-3 py-1 bg-slate-800/50 text-slate-400 text-xs rounded-full border border-slate-700/50">
                            Ward Personality: <span className="text-white font-bold">{analysis.personalityType}</span>
                        </span>
                    </div>
                </div>

            </div>
        </div>
    );
};


// ====================================================================================
//
//      NEW NARRATIVE SUB-COMPONENTS (WITH N/A HANDLING)
//
// ====================================================================================

// --- EXPANDABLE CARD FOR SCROLLABLE CONTENT ---
// UPDATED: Now accepts 'contentToCopy' prop
export const ExpandableCard = ({ title, icon: Icon, children, className = "", contentToCopy = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // New Copy Handler
  const handleCopy = async () => {
    if (!contentToCopy) return;
    try {
        await navigator.clipboard.writeText(contentToCopy);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
        console.error("Failed to copy text", err);
    }
  };

  return (
    <>
      <div className={`bg-slate-900/40 rounded-lg p-4 flex flex-col relative group border border-slate-700/50 hover:border-slate-600 transition-colors h-64 overflow-hidden ${className}`}>
        <div className="flex items-center justify-between mb-3 flex-shrink-0">
          <div className="flex items-center space-x-2">
            {Icon && <Icon size={18} className="text-slate-400" />}
            <h4 className="font-semibold text-slate-200">{title}</h4>
          </div>
          <button 
            onClick={() => setIsOpen(true)}
            className="text-slate-500 hover:text-accent-400 transition-colors p-1"
            title="Expand View"
          >
            <Maximize2 size={14} />
          </button>
        </div>
        
        {/* Preview Content */}
        <div className="relative flex-grow overflow-y-auto pr-2 custom-scrollbar">
            {children}
        </div>
        {/* Fade overlay for overflowing text */}
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none rounded-b-lg"></div>
      </div>

      {/* MODAL OVERLAY */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-10">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col relative z-50 overflow-hidden"
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-800">
                <div className="flex items-center space-x-3">
                  {Icon && <Icon size={24} className="text-accent-400" />}
                  <h3 className="text-xl font-bold text-white">{title}</h3>
                </div>
                
                {/* MODAL ACTIONS: Copy (if enabled) + Close */}
                <div className="flex items-center gap-3">
                    {/* CONDITIONAL COPY BUTTON */}
                    {contentToCopy && (
                        <button
                            onClick={handleCopy}
                            className={`
                                flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all
                                ${isCopied 
                                    ? "bg-green-500/20 text-green-400 border border-green-500/50" 
                                    : "bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700"}
                            `}
                        >
                            {isCopied ? <Check size={14} /> : <Copy size={14} />}
                            {isCopied ? "Copied" : "Copy"}
                        </button>
                    )}

                    <button 
                      onClick={() => setIsOpen(false)}
                      className="text-slate-400 hover:text-white transition-colors bg-slate-800/50 hover:bg-slate-800 rounded-lg p-2"
                    >
                      <X size={20} />
                    </button>
                </div>

              </div>
              <div className="p-8 overflow-y-auto">
                {children}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

// --- ROBUST VIDEO SCRIPT VIEWER (UPDATED) ---
export const VideoScriptViewer = ({ script }) => {
  if (!script) return <p className="text-sm text-slate-400 italic">No script available.</p>;

  const parseScript = (scriptString) => {
    // 1. Clean Header & Normalize
    // We replace the header and also insure standard spacing around bold tags to help regex
    let cleanContent = scriptString
      .replace(/###\s*(VISUAL|VIDEO|SCENE)\s*\|\s*AUDIO/i, '')
      .trim();

    // 2. INTELLIGENT SCENE SPLITTING
    // Split by "1. ", "2. " OR "**Title**"
    // The lookahead (?=...) ensures we don't consume the number/title during the split
    let segments = cleanContent.split(/(?=\d+\.\s+\*\*)|(?=\n\d+\.\s)/).filter(s => s.trim().length > 0);

    // FALLBACK: If numbers aren't present or regex failed to split tight strings, 
    // try splitting by just double asterisks (Headers)
    if (segments.length < 2) {
        segments = cleanContent.split(/(?=\*\*[A-Za-z0-9\s\(\)-]+\*\*)/).filter(s => s.trim().length > 0);
    }

    return segments.map((segment, index) => {
        // --- A. EXTRACT TITLE ---
        // Look for text wrapped in **...** at the start
        // Matches: "1. **The Jolt (0-5s)**" or just "**The Jolt**"
        const titleMatch = segment.match(/^[\d\.]*\s*\*\*(.*?)\*\*/);
        
        let title = '';
        if (titleMatch) {
            title = titleMatch[1].trim();
        } else {
            // Fallback: Use first few words if no bold title found
            const firstLine = segment.split(/[:\n]/)[0];
            title = firstLine.length < 50 ? firstLine.replace(/^\d+\.\s*/, '') : `Scene ${index + 1}`;
        }

        // --- B. CLEAN CONTENT BODY ---
        // Remove the Number and Title we just found from the text body to process the rest
        let body = segment.replace(/^[\d\.]*\s*(\*\*.*?\*\*)?/, '').trim();

        // --- C. SPLIT VISUAL vs AUDIO ---
        // We look for Audio tags first, as they are usually the definitive separator.
        // Supports: "Audio:", "Audio Narration:", "VO:", "Voiceover:", "Sound:"
        const audioTagMatch = body.match(/(?:Audio|Sound|VO|Voiceover|Narration)[\w\s]*:/i);

        let visualText = '';
        let audioText = '';

        if (audioTagMatch) {
            // CASE 1: Audio Tag Found
            
            // Audio = Everything AFTER the tag
            audioText = body.substring(audioTagMatch.index + audioTagMatch[0].length).trim();
            
            // Visual = Everything BEFORE the tag
            const preAudioContent = body.substring(0, audioTagMatch.index).trim();
            
            // Clean "Visual:" tag from the pre-audio content if it exists
            // Supports: "Visual:", "Video:", "Scene:"
            visualText = preAudioContent.replace(/^(?:Visual|Video|Scene)[\w\s]*:\s*/i, '').trim();

        } else {
            // CASE 2: No Audio Tag Found (Visual Only or Unformatted)
            
            // Check if there is a generic "Visual:" tag to clean up
            const visualTagMatch = body.match(/^(?:Visual|Video|Scene)[\w\s]*:/i);
            
            if (visualTagMatch) {
                 visualText = body.substring(visualTagMatch.index + visualTagMatch[0].length).trim();
            } else {
                 // Absolute fallback: If no tags exist, assume it's visual description 
                 // (unless it's wrapped in quotes, which implies dialogue, but that's risky to assume)
                 visualText = body;
            }
        }

        // --- D. FINAL CLEANUP ---
        // Remove wrapping quotes or artifacts
        visualText = visualText.replace(/^["*]+|["*]+$/g, '');
        audioText = audioText.replace(/^["*]+|["*]+$/g, '');

        return { title, visual: visualText, audio: audioText };
    });
  };

  const parsedData = parseScript(script);

  if (parsedData.length === 0) return <p className="text-sm text-slate-300 whitespace-pre-wrap">{script}</p>;

  return (
    <div className="overflow-x-auto h-full">
      <table className="min-w-full divide-y divide-slate-700/50 text-sm table-fixed">
        <thead className="bg-slate-800/80 sticky top-0 z-10 backdrop-blur-sm">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider w-[20%]">Scene</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider w-[40%]">Visual</th>
            <th className="px-4 py-3 text-left text-xs font-bold text-slate-300 uppercase tracking-wider w-[40%]">Audio / Dialogue</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/30 bg-slate-800/20">
          {parsedData.map((row, index) => (
            <tr key={index} className="hover:bg-slate-700/30 transition-colors group">
              <td className="px-4 py-3 align-top text-xs font-bold text-white border-r border-slate-700/30 break-words">
                {row.title}
              </td>
              <td className="px-4 py-3 align-top text-xs text-slate-300 leading-relaxed whitespace-pre-wrap break-words">
                {row.visual || <span className="text-slate-600 italic">--</span>}
              </td>
              <td className="px-4 py-3 align-top text-xs text-slate-300 italic leading-relaxed whitespace-pre-wrap border-l border-slate-700/50 bg-slate-800/20 break-words">
                {row.audio ? `"${row.audio}"` : <span className="text-slate-500 not-italic opacity-50">N/A</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


export const CommunicationStyle = ({ style }) => {
  const { tone, language, format, channels } = style || {};
  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-slate-800/50 p-3 rounded-lg">
          <div className="text-slate-400 text-xs uppercase mb-1">Tone</div>
          <div className="text-white font-medium">{tone || 'N/A'}</div>
        </div>
        <div className="bg-slate-800/50 p-3 rounded-lg">
          <div className="text-slate-400 text-xs uppercase mb-1">Language</div>
          <div className="text-white font-medium">{language || 'N/A'}</div>
        </div>
      </div>
      <div>
        <span className="text-slate-400 block mb-2">Preferred Format:</span>
        <span className="text-white font-medium bg-slate-800/50 px-3 py-1.5 rounded-md block border-l-2 border-accent-500">
            {format || 'N/A'}
        </span>
      </div>
      <div className="pt-3 border-t border-slate-600/30">
        <span className="text-slate-400 text-xs block mb-2">Key Channels:</span>
        <div className="flex flex-wrap gap-2">
          {(channels && channels.length > 0) ? channels.map((channel, i) => (
            <div key={i} className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-medium border bg-blue-500/10 text-blue-400 border-blue-500/20">
              <RadioTower size={12} />
              <span>{channel}</span>
            </div>
          )) : (
            <p className="text-xs text-slate-400 italic">No key channels specified.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const KeyIssues = ({ issues }) => {
  return (
    <div className="space-y-3">
      {(issues && issues.length > 0) ? issues.sort((a, b) => a.priority - b.priority).map((issue, i) => (
        <div key={`${issue.issue}-${i}`} className="bg-slate-800/50 p-3 rounded-md border-l-2 border-transparent hover:border-yellow-500 transition-colors">
          <h5 className="font-semibold text-white mb-1 flex items-baseline">
            <span className="text-yellow-500 font-mono text-xs mr-2">#{issue.priority}</span>
            {issue.issue}
          </h5>
          <p className="text-sm text-slate-300 mb-2">{issue.description}</p>
          <p className="text-xs text-slate-500 italic flex items-center">
            <MapPin size={10} className="mr-1" />
            {issue.geographic_context}
          </p>
        </div>
      )) : (
        <p className="text-sm text-slate-400 italic">No key issues available.</p>
      )}
    </div>
  );
};

export const PersuasionApproach = ({ approaches }) => {
  return (
    <div className="space-y-3">
      {(approaches && approaches.length > 0) ? approaches.map((approach, i) => (
        <div key={i} className="bg-slate-800/50 p-3 rounded-md">
          <div className="flex justify-between items-center mb-2">
            <ScriptTag text={approach.method} color="blue" icon={Tags} />
            {approach.score && (
              <span className="text-xs font-bold text-accent-300 bg-accent-500/10 px-2 py-0.5 rounded">
                {(approach.score * 100).toFixed(0)}% Receptivity
              </span>
            )}
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">{approach.description}</p>
        </div>
      )) : (
        <p className="text-sm text-slate-400 italic">No persuasion approaches available.</p>
      )}
    </div>
  );
};

export const ConversationStarters = ({ starters }) => {
  return (
    <div className="space-y-2">
      {(starters && starters.length > 0) ? (
        starters.map((item, i) => (
          <div key={i} className="flex items-start bg-slate-800/40 p-2 rounded hover:bg-slate-800/60 transition-colors">
             <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300 mr-3 mt-0.5">
               {i+1}
             </div>
             <p className="text-sm text-slate-300 italic">"{item.replace(/"\d\. "/g, '').replace(/"$/g, '')}"</p>
          </div>
        ))
      ) : (
        <p className="text-sm text-slate-400 italic">N/A</p>
      )}
    </div>
  );
};

export const TypicalConcerns = ({ concerns }) => {
  return (
    <ul className="space-y-2">
      {(concerns && concerns.length > 0) ? (
        concerns.map((item, i) => (
          <li key={i} className="flex items-start text-sm text-slate-300">
             <AlertTriangle size={14} className="text-red-400 mr-2 mt-1 flex-shrink-0" />
             <span>{item.replace(/"\d\. "/g, '').replace(/"$/g, '')}</span>
          </li>
        ))
      ) : (
        <p className="text-sm text-slate-400 italic">N/A</p>
      )}
    </ul>
  );
};

// ====================================================================================
// REFACTORED NARRATIVE PANEL (Glass Container + Linear Flow)
// ====================================================================================

export const NarrativePanel = ({ narrativeData }) => {
  const {
    headline,
    elevator_pitch,
    corpus_narrative,
    video_script,
    communication_implication,
    communication_style,
    key_issues,
    persuasion_approach,
    conversation_starters,
    typical_concerns,
  } = narrativeData || {}; 

  return (
    <GlassContainer title="Strategic Narrative & Engagement Guide" icon={Compass}>
      <div className="space-y-8">
        
        {/* SECTION 1: THE HOOK (HEADLINE) */}
        {headline && (
           <div className="text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400 leading-tight">
             {headline.replace(/^"|"$/g, '')}
           </div>
        )}

        <div className="h-px bg-slate-700/50 w-full"></div>

        {/* SECTION 2: CORPUS NARRATIVE (FULL WIDTH) */}
        {/* UPDATED: Added contentToCopy prop */}
        <div className="w-full">
            <ExpandableCard 
              title="Corpus Narrative" 
              icon={BookOpen} 
              className="h-64"
              contentToCopy={corpus_narrative}
            >
                {corpus_narrative ? (
                  <div className="space-y-2">
                    {corpus_narrative.split('\n').filter(p => p.trim() !== '').map((para, i) => (
                      <p key={i} className="text-sm text-slate-300 leading-relaxed">
                        {para.replace(/\*\*/g, '')}
                      </p>
                    ))}
                  </div>
                ) : (
                <p className="text-sm text-slate-400 italic">N/A</p>
                )}
            </ExpandableCard>
        </div>

        {/* SECTION 3: PITCH & SCRIPT (GRID) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
           {/* LEFT: Elevator Pitch */}
           <div className="h-64 flex flex-col bg-slate-900/40 rounded-lg p-4 border border-slate-700/50">
             <div className="flex items-center space-x-2 mb-3 text-slate-200 flex-shrink-0">
                <Quote size={18} />
                <h4 className="font-semibold">Elevator Pitch</h4>
             </div>
             {elevator_pitch ? (
               <div className="flex-grow min-h-0 relative bg-gradient-to-br from-accent-500/10 to-transparent border-l-4 border-accent-500 rounded-r-lg group">
                 <div className="absolute inset-0 overflow-hidden p-4 flex items-center justify-center">
                   <p className="text-xs lg:text-sm text-white font-medium italic text-center leading-snug line-clamp-6 lg:line-clamp-8">
                     "{elevator_pitch.replace(/^"|"\s\([^)]+\)|"$/g, '').replace(/^\*\*Doorstep Line:\*\* /, '')}"
                   </p>
                 </div>
               </div>
             ) : (
               <p className="text-sm text-slate-400 italic">No pitch available.</p>
             )}
           </div>

           {/* RIGHT: Video Script */}
           <ExpandableCard title="Video Script Draft" icon={PlayCircle} className="h-64">
             <VideoScriptViewer script={video_script} />
           </ExpandableCard>
        </div>

        {/* SECTION 4: ENGAGEMENT TACTICS (3-COL GRID WITH CONSTRAINED HEIGHTS) */}
        <div className="space-y-4">
           <h4 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-700/50 pb-2">Engagement Tactics</h4>
           
           <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <ExpandableCard title="Key Issues" icon={List} className="h-64">
                 <KeyIssues issues={key_issues} />
              </ExpandableCard>
              
              <ExpandableCard title="Comm. Style" icon={Megaphone} className="h-64">
                 <CommunicationStyle style={communication_style} />
              </ExpandableCard>
              
              <ExpandableCard title="Persuasion Approach" icon={GitMerge} className="h-64">
                 <PersuasionApproach approaches={persuasion_approach?.approaches} />
              </ExpandableCard>
              
              <ExpandableCard title="Typical Concerns" icon={AlertTriangle} className="h-64">
                 <TypicalConcerns concerns={typical_concerns} />
              </ExpandableCard>
              
              <ExpandableCard title="Conversation Starters" icon={MessageCircle} className="h-64">
                 <ConversationStarters starters={conversation_starters} />
              </ExpandableCard>

              {/* Communication Implication */}
              <div className="h-64 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 overflow-y-auto custom-scrollbar">
                  <div className="flex items-start space-x-2 mb-2">
                      <Info size={18} className="text-blue-400 mt-1 flex-shrink-0" />
                      <h5 className="font-semibold text-blue-400">Communication Implication</h5>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {communication_implication || 'N/A'}
                  </p>
              </div>

           </div>
        </div>

      </div>
    </GlassContainer>
  );
};